'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

const TZ = 'Asia/Tomsk';

function getHHMM(ts: number, timeFmt: Intl.DateTimeFormat) {
  const parts = timeFmt.formatToParts(new Date(ts));
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '--';
  const mm = parts.find((p) => p.type === 'minute')?.value ?? '--';
  return { hh, mm };
}

export default function HeaderClock() {
  const [ts, setTs] = useState(() => Date.now());

  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat('ru-RU', {
        timeZone: TZ,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
    [],
  );

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat('ru-RU', {
        timeZone: TZ,
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [],
  );

  const { hh, mm } = getHHMM(ts, timeFmt);

  const prevMmRef = useRef(mm);
  const [prevMm, setPrevMm] = useState(mm);
  const [flip, setFlip] = useState(false);

  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  // тик строго по минутам
  useEffect(() => {
    const tick = () => setTs(Date.now());

    const start = () => {
      tick();
      intervalRef.current = window.setInterval(tick, 60_000);
    };

    const msToNextMinute = 60_000 - (Date.now() % 60_000);
    timeoutRef.current = window.setTimeout(start, msToNextMinute);

    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      timeoutRef.current = null;
      intervalRef.current = null;
    };
  }, []);

  // flip на смене минут
  useEffect(() => {
    if (prevMmRef.current !== mm) {
      setPrevMm(prevMmRef.current);
      prevMmRef.current = mm;

      setFlip(true);
      const id = window.setTimeout(() => setFlip(false), 520);
      return () => window.clearTimeout(id);
    }
  }, [mm]);

  const d = new Date(ts);

  return (
    <div className="inline-flex flex-col gap-0.5">
      <time
        dateTime={d.toISOString()}
        className="text-3xl font-semibold md:text-3xl xl:text-4xl tabular-nums leading-none"
        suppressHydrationWarning
        aria-label="Томское время"
      >
        <span>{hh}</span>

        <span className="blink-colon mx-1" aria-hidden="true">
          :
        </span>

        <span className={`flip-unit ${flip ? 'is-flipping' : ''}`} aria-label={`Минуты: ${mm}`}>
          <span className="flip-static">{mm}</span>
          <span className="flip-top" aria-hidden="true">
            {prevMm}
          </span>
          <span className="flip-bottom" aria-hidden="true">
            {mm}
          </span>
        </span>

        {/* <span className="ml-2 align-middle text-[10px] font-medium text-[#eed023] opacity-90">
          Томск
        </span> */}
      </time>

      <div className="text-xs text-[#eed023] md:text-sm" suppressHydrationWarning>
        {dateFmt.format(d)}
      </div>
    </div>
  );
}
